'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { CareAndShareContainer } from './container'
import { Marquee, MarqueeItem } from './marquee'

interface CustomerNamesCloudProps {
  names: string[]
  className?: string
}

// Utility function để chia array thành chunks
const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

export const CustomerNamesCloud = ({
  names,
  className,
}: CustomerNamesCloudProps) => {
  const sizes = [
    { class: 'text-xl', mobile: 'text-base' },
    { class: 'text-3xl', mobile: 'text-xl' },
    { class: 'text-2xl', mobile: 'text-lg' },
    { class: 'text-xl', mobile: 'text-sm' },
    { class: 'text-5xl', mobile: 'text-2xl' },
  ]

  const colors = [
    'text-blue-600', // blue (#2F5ACF)
    'text-neutral-900', // back (black)
    'text-yellow-500', // yellow (#F9C81A)
    'text-blue-600', // red -> blue (keeping consistent)
  ]

  // Deterministic "random" getter
  const getDeterministicItem = (
    name: string,
    index: number,
    rowIndex: number
  ) => {
    const seed = name.length + index + rowIndex
    const size = sizes[seed % sizes.length]
    const color = colors[(seed * 7) % colors.length]
    return { size, color }
  }

  // Chia names thành 4 hàng
  const nameChunks = chunkArray(names, Math.ceil(names.length / 4))

  return (
    <div
      className={cn(
        'w-full rounded-b-3xl border border-primary-500 py-6 md:py-10',
        className
      )}
    >
      <div className='flex flex-col gap-2 md:gap-4'>
        {nameChunks.map((chunk, rowIndex) => (
          <div key={rowIndex} className='overflow-hidden'>
            <Marquee speed={'slow'} direction='left' repeat={3} className='py-2'>
              {chunk.map((name, index) => {
                const { size, color } = getDeterministicItem(
                  name,
                  index,
                  rowIndex
                )
                return (
                  <MarqueeItem key={`${name}-${index}`} className='mx-2'>
                    <div
                      className={cn(
                        'flex items-center justify-center whitespace-nowrap rounded-xl bg-white px-2 py-1 shadow-md',
                        'shadow-[0px_0px_4px_rgba(0,0,0,0.06),4px_4px_27px_rgba(0,0,0,0.12)]',
                        size.class,
                        `lg:${size.class}`,
                        `${size.mobile}`,
                        color
                      )}
                    >
                      {name}
                    </div>
                  </MarqueeItem>
                )
              })}
            </Marquee>
          </div>
        ))}
      </div>
    </div>
  )
}

// Component với dữ liệu mẫu từ code gốc
export const CareShareCustomerNames = () => {
  const t = useTranslations()

  const customerNames = [
    'Thái',
    'Trí',
    'Trọng',
    'Nguyên',
    'Phúc',
    'Đạt',
    'Nguyễn',
    'Đạt',
    'Huy',
    'Nguyễn',
    'Dương',
    'Tường',
    'Khải',
    'An',
    'Tín',
    'Minh',
    'Nam',
    'Tùng',
    'qcuongphamhp',
    'Phạm',
    'Trần',
    'Tiến',
    'Thông',
    'lê',
    'Khoa',
    'Tai',
    'Việt',
    'Dang',
    'Hoàng',
    'Vinh',
    'Đức',
    'Tuấn',
    'Biêu',
    'Trang',
    'Thuận',
    'Khoa',
    'Phúc',
    'Tín',
    'Đức',
    'Thicker',
    'Cường',
    'Duy',
    'Dang',
    'Hiền',
    'Tiến',
    'Nguyen',
    'Nghiệp',
    'Trân',
    'Ngân',
    'Liêm',
    'Phạm',
    'Đăng',
    'Minh',
    'Truyền',
    'Tuấn',
    'Huy',
    'Ninh',
    'Vân',
    'Nghĩa',
    'Thomas',
    'Huyền',
    'Phan',
    'Thủy',
    'Tung',
    'Quý',
    'Nguyễn',
    'Lý',
    'Anh',
    'TRUNG',
    'Thắng',
    'Tan',
    'Tuyến',
    'Ngọc',
    'Anh',
    'Dương',
    'Hiếu',
    'Luan',
    'Nhân',
    'Kỳ',
    'Hưng',
    'Sơn',
    'Lê',
    'Anh',
    'Hằng',
    'Tin',
    'Tuấn',
    'Anh',
    'Long',
    'Cường',
    'Thế',
    'Hoà',
    'Sơn',
    'Trà',
    'Sơn',
    'Anh',
    'Tiennn',
    'Khoa',
    'Tùng',
    'Hoài',
    'Hạnh',
  ]

  return (
    <CareAndShareContainer className='py-8 lg:py-12'>
      <div className='rounded-t-3xl bg-primary-500 py-5 text-center'>
        <div className='flex items-center justify-center gap-4'>
          <h3 className='font-criteria text-lg font-bold uppercase leading-5 tracking-wide text-white lg:text-4xl lg:leading-tight'>
            {t('care_and_share_hardcode.customer_names.title')}
          </h3>
          <Image
            src={ImageHelper.getImageUrlCdn(
              '/image/February2023/mceclip0_53.png'
            )}
            alt={t('care_and_share_hardcode.customer_names.logo_alt')}
            width={48}
            height={48}
            className='h-8 w-auto lg:h-12'
          />
        </div>
      </div>
      <CustomerNamesCloud names={customerNames} />
    </CareAndShareContainer>
  )
}
